import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

// Basic selectors
export const selectCandidatesState = (state: RootState) => state.candidates;
export const selectCandidates = (state: RootState) => state.candidates.candidates;
export const selectCurrentCandidate = (state: RootState) => state.candidates.currentCandidate;
export const selectRecentCandidates = (state: RootState) => state.candidates.recentCandidates;
export const selectCandidatesByStage = (state: RootState) => state.candidates.candidatesByStage;
export const selectCandidatesByJob = (state: RootState) => state.candidates.candidatesByJob;

// Loading selectors
export const selectCandidatesLoading = (state: RootState) => state.candidates.loading;
export const selectCandidateLoading = (state: RootState) => state.candidates.candidateLoading;
export const selectDocumentsLoading = (state: RootState) => state.candidates.documentsLoading;
export const selectActivitiesLoading = (state: RootState) => state.candidates.activitiesLoading;
export const selectNotesLoading = (state: RootState) => state.candidates.notesLoading;

// Error selectors
export const selectCandidatesError = (state: RootState) => state.candidates.error;
export const selectCandidateError = (state: RootState) => state.candidates.candidateError;

// Document, activity, and notes selectors
export const selectCandidateDocuments = (state: RootState) => state.candidates.documents;
export const selectCandidateActivities = (state: RootState) => state.candidates.activities;
export const selectCandidateNotes = (state: RootState) => state.candidates.notes;

// Pagination
export const selectPagination = (state: RootState) => state.candidates.pagination;
export const selectLastFetched = (state: RootState) => state.candidates.lastFetched;
export const selectSearchCache = (state: RootState) => state.candidates.searchCache;

// Memoized selectors
export const selectCandidatesSortedByName = createSelector(
    [selectCandidates],
    (candidates) => [...candidates].sort((a, b) => {
        const nameA = `${a.lastName}, ${a.firstName}`.toLowerCase();
        const nameB = `${b.lastName}, ${b.firstName}`.toLowerCase();
        return nameA.localeCompare(nameB);
    })
);

export const selectCandidatesSortedByDate = createSelector(
    [selectCandidates],
    (candidates) => [...candidates].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
);

export const selectCandidateById = createSelector(
    [selectCandidates, (_, candidateId: string) => candidateId],
    (candidates, candidateId) => candidates.find(candidate => candidate.id === candidateId)
);

export const selectCandidatesForStage = createSelector(
    [selectCandidatesByStage, (_, stageId: string) => stageId],
    (candidatesByStage, stageId) => candidatesByStage[stageId] || []
);

export const selectCandidatesForJob = createSelector(
    [selectCandidatesByJob, (_, jobId: string) => jobId],
    (candidatesByJob, jobId) => candidatesByJob[jobId] || []
);

export const selectDocumentsForCandidate = createSelector(
    [selectCandidateDocuments, (_, candidateId: string) => candidateId],
    (documents, candidateId) => documents[candidateId] || []
);

export const selectActivitiesForCandidate = createSelector(
    [selectCandidateActivities, (_, candidateId: string) => candidateId],
    (activities, candidateId) => activities[candidateId] || []
);

export const selectNotesForCandidate = createSelector(
    [selectCandidateNotes, (_, candidateId: string) => candidateId],
    (notes, candidateId) => notes[candidateId] || []
);

export const selectDocumentsLoadingForCandidate = createSelector(
    [selectDocumentsLoading, (_, candidateId: string) => candidateId],
    (documentsLoading, candidateId) => documentsLoading[candidateId] || false
);

export const selectActivitiesLoadingForCandidate = createSelector(
    [selectActivitiesLoading, (_, candidateId: string) => candidateId],
    (activitiesLoading, candidateId) => activitiesLoading[candidateId] || false
);

export const selectNotesLoadingForCandidate = createSelector(
    [selectNotesLoading, (_, candidateId: string) => candidateId],
    (notesLoading, candidateId) => notesLoading[candidateId] || false
);

// Statistics selectors
export const selectCandidateStats = createSelector(
    [selectCandidates, selectRecentCandidates],
    (allCandidates, recentCandidates) => ({
        total: allCandidates.length,
        recentCount: recentCandidates.length,
        byStage: allCandidates.reduce((acc, candidate) => {
            const stageId = candidate.stageId;
            acc[stageId] = (acc[stageId] || 0) + 1;
            return acc;
        }, {} as Record<string, number>),
        byJob: allCandidates.reduce((acc, candidate) => {
            const jobId = candidate.jobId;
            if (jobId) {
                acc[jobId] = (acc[jobId] || 0) + 1;
            }
            return acc;
        }, {} as Record<string, number>)
    })
);

export const selectCandidateCountByStage = createSelector(
    [selectCandidates, (_, stageId: string) => stageId],
    (candidates, stageId) => candidates.filter(c => c.stageId === stageId).length
);

export const selectCandidateCountByJob = createSelector(
    [selectCandidates, (_, jobId: string) => jobId],
    (candidates, jobId) => candidates.filter(c => c.jobId === jobId).length
);

// Search and filtering selectors
export const selectFilteredCandidates = createSelector(
    [
        selectCandidates,
        (_, filters: {
            searchTerm?: string;
            stageId?: string;
            jobId?: string;
            source?: string;
        }) => filters
    ],
    (candidates, filters) => {
        let filtered = candidates;

        if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(candidate => {
                const fullName = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
                const email = candidate.email.toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            });
        }

        if (filters.stageId) {
            filtered = filtered.filter(candidate => candidate.stageId === filters.stageId);
        }

        if (filters.jobId) {
            filtered = filtered.filter(candidate => candidate.jobId === filters.jobId);
        }

        if (filters.source) {
            filtered = filtered.filter(candidate => candidate.source === filters.source);
        }

        return filtered;
    }
);

export const selectSortedAndFilteredCandidates = createSelector(
    [
        selectFilteredCandidates,
        (_, filters: any, sortBy: string = 'lastName', sortDirection: 'asc' | 'desc' = 'asc') => ({ sortBy, sortDirection })
    ],
    (candidates, { sortBy, sortDirection }) => {
        return [...candidates].sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'firstName':
                    comparison = a.firstName.localeCompare(b.firstName);
                    break;
                case 'lastName':
                    comparison = a.lastName.localeCompare(b.lastName);
                    break;
                case 'email':
                    comparison = a.email.localeCompare(b.email);
                    break;
                case 'createdAt':
                    comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    break;
                case 'daysInStage':
                    comparison = a.daysInCurrentStage - b.daysInCurrentStage;
                    break;
                case 'stageName':
                    comparison = (a.currentStage || '').localeCompare(b.currentStage || '');
                    break;
                case 'jobTitle':
                    comparison = (a.jobTitle || '').localeCompare(b.jobTitle || '');
                    break;
                default:
                    comparison = a.lastName.localeCompare(b.lastName);
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }
);

// Data staleness selectors
export const selectIsDataStaleForKey = createSelector(
    [selectLastFetched, (_, key: string, maxAge: number = 5 * 60 * 1000) => ({ key, maxAge })],
    (lastFetched, { key, maxAge }) => {
        const timestamp = lastFetched[key];
        if (!timestamp) return true;
        return Date.now() - timestamp > maxAge;
    }
);

export const selectIsAllCandidatesStale = createSelector(
    [selectLastFetched],
    (lastFetched) => {
        const timestamp = lastFetched['all'];
        if (!timestamp) return true;
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return timestamp < fiveMinutesAgo;
    }
);

export const selectIsRecentCandidatesStale = createSelector(
    [selectLastFetched],
    (lastFetched) => {
        const timestamp = lastFetched['recent'];
        if (!timestamp) return true;
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return timestamp < fiveMinutesAgo;
    }
);

export const selectIsCandidatesByStageStale = createSelector(
    [selectLastFetched, (_, stageId: string) => stageId],
    (lastFetched, stageId) => {
        const timestamp = lastFetched[`stage-${stageId}`];
        if (!timestamp) return true;
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return timestamp < fiveMinutesAgo;
    }
);

// Advanced selectors for analytics
export const selectCandidatesByStageStats = createSelector(
    [selectCandidates],
    (candidates) => {
        const stats: Record<string, { count: number; avgDays: number; candidates: string[] }> = {};

        candidates.forEach(candidate => {
            const stageId = candidate.stageId;
            if (!stats[stageId]) {
                stats[stageId] = {
                    count: 0,
                    avgDays: 0,
                    candidates: []
                };
            }

            stats[stageId].count++;
            stats[stageId].candidates.push(candidate.id);
        });

        // Calculate average days for each stage
        Object.keys(stats).forEach(stageId => {
            const stageCandidates = candidates.filter(c => c.stageId === stageId);
            const totalDays = stageCandidates.reduce((sum, c) => sum + c.daysInCurrentStage, 0);
            stats[stageId].avgDays = stats[stageId].count > 0 ? Math.round(totalDays / stats[stageId].count) : 0;
        });

        return stats;
    }
);

export const selectCandidatesBySource = createSelector(
    [selectCandidates],
    (candidates) => {
        return candidates.reduce((acc, candidate) => {
            const source = candidate.source || 'Unknown';
            acc[source] = (acc[source] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);
    }
);

export const selectTopPerformingStages = createSelector(
    [selectCandidatesByStageStats],
    (stageStats) => {
        return Object.entries(stageStats)
            .sort(([, a], [, b]) => b.count - a.count)
            .slice(0, 5)
            .map(([stageId, stats]) => ({
                stageId,
                count: stats.count,
                avgDays: stats.avgDays
            }));
    }
);

// Helper selector to check if a candidate exists in current state
export const selectCandidateExists = createSelector(
    [selectCandidates, (_, candidateId: string) => candidateId],
    (candidates, candidateId) => candidates.some(c => c.id === candidateId)
);

// Selector for candidates requiring action (e.g., been in stage too long)
export const selectCandidatesRequiringAction = createSelector(
    [selectCandidates],
    (candidates) => candidates.filter(candidate => {
        // Define business rules for candidates requiring action
        const maxDaysInStage = 30; // configurable
        return candidate.daysInCurrentStage > maxDaysInStage;
    })
);

export const selectCandidatesWithUpcomingInterviews = createSelector(
    [selectCandidates],
    (candidates) => candidates.filter(candidate => {
        if (!candidate.interviewDate) return false;
        const interviewDate = new Date(candidate.interviewDate);
        const today = new Date();
        const daysUntilInterview = Math.ceil((interviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilInterview >= 0 && daysUntilInterview <= 7; // Next 7 days
    })
);